import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { mailboxPath } from '../mail/urls';
import MailShell, { NavButton, useMail } from '../components/MailShell';
import { bumpRef, dropRef } from '../mail/useMailboxes';
import { KEYWORD, type Email as JmapEmail } from '../api/types';
import EmptyFolderDialog from '../components/EmptyFolderDialog';
import FolderPicker from '../components/FolderPicker';
import MessageList from '../components/MessageList';
import Reader from '../components/Reader';
import Resizer from '../components/Resizer';
import Toolbar from '../components/Toolbar';
import { useKeys } from '../keys/store';
import { rowUnread, useMailActions, type BulkAction } from '../mail/actions';
import { displayLabel, resolveSpecial } from '../mail/folders';
import { useMailboxList } from '../mail/useMailboxList';
import { DEFAULT_SORT, SORTS, type Sort, type SortField } from '../mail/search';
import { config } from '../config/app';
import { useT } from '../i18n';
import { useTheme } from '../theme/store';
import { overlayOpen } from '../ui/overlays';
import { usePanes } from '../ui/viewport';

/*
    Sidebar + message list + reading pane. Where the reading pane sits is readingPos —
    except on a window too narrow to hold both, where there is nowhere for it to sit.
*/

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), Math.max(min, max));

const bulkButton: React.CSSProperties = {
    height: 24,
    padding: '0 9px',
    background: 'var(--surface)',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text-dim)',
    fontSize: 'var(--fs-xs)',
};

/*
    The shell provides the context, so it cannot be rendered by the same
    component that consumes it — hence a wrapper around a panel.
*/
export default function MailboxView() {
    const { mbox } = useParams();
    return (
        <MailShell section="mail" selectedMailboxId={mbox}>
            <MailboxPanel />
        </MailShell>
    );
}

function MailboxPanel() {
    const { t, num } = useT();
    const { mbox, email } = useParams();
    const navigate = useNavigate();
    const { account, client, mailboxes, reloadFolders, compose: openCompose, search, focusSearch } = useMail();
    const layout = useTheme((s) => s.layout);
    const openFullRef = useRef<JmapEmail | null>(null);
    const lookup = useKeys((s) => s.lookup);
    const lookupRef = useRef(lookup);
    lookupRef.current = lookup;

    /*
        The search box is in the top bar now, so both the text and the debounce live in the shell.
        This screen only reads the settled value — one copy, and it cannot drift from what the
        person is looking at while they type.
    */


    /*
        Special folders come from resolveSpecial, not from a role lookup.

        This server's "Archive" carries role null — the import never set one — so
        `find(m => m.role === 'archive')` was undefined, canArchive was false, and
        both the button and the shortcut silently did nothing. resolveSpecial falls
        back to the name and the sidebar offers to write the role for good.
    */
    const special = useMemo(() => resolveSpecial(mailboxes), [mailboxes]);
    const inboxId = special.byRole.inbox?.id;
    const archiveId = special.byRole.archive?.id;
    const trashId = special.byRole.trash?.id;
    const junkId = special.byRole.junk?.id;
    const sentId = special.byRole.sent?.id;

    // No folder in the URL yet: land on the inbox once we know its id.
    useEffect(() => {
        if (!account || mbox || !mailboxes.length) return;
        const landing = inboxId ?? mailboxes[0]?.id;
        if (landing) navigate(mailboxPath(account.id, landing), { replace: true });
    }, [account, mbox, mailboxes, inboxId, navigate]);

    const threads = useTheme((s) => s.threads);
    const listWidth = useTheme((s) => s.listWidth);
    const listHeight = useTheme((s) => s.listHeight);
    const setPaneSize = useTheme((s) => s.setPaneSize);
    const readingPos = useTheme((s) => s.readingPos);
    const { onePane } = usePanes();
    const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
    const list = useMailboxList(client, account?.accountId, mbox, search, threads, mailboxes, sort);
    const [selection, setSelection] = useState<Set<string>>(new Set());
    const anchor = useRef<number | null>(null);
    const current = mailboxes.find((m) => m.id === mbox);
    const [emptying, setEmptying] = useState(false);

    // A selection belongs to the list it was made in; changing folder, search or
    // order leaves it pointing at messages that are no longer on screen.
    useEffect(() => {
        setSelection(new Set());
        anchor.current = null;
    }, [mbox, search, sort]);

    const toggleSelect = useCallback(
        (id: string, index: number, extend: boolean) => {
            /*
                Çıpa, TAŞINMADAN ÖNCE okunuyor ve güncelleyiciye ref değil değeri veriliyor.

                Ölçüldü (2026-09-04, tarayıcı turu): eskiden `anchor.current = index` satırı
                `setSelection`in hemen ARDINDAN koşuyordu, React ise güncelleyiciyi render
                sırasında çağırır — yani aralık hesaplanana kadar çıpa çoktan tıklanan satıra
                kaymış oluyordu ve aralık tek satıra çöküyordu. Yorumun söz verdiği "shift ile
                kırk iletiyi seç" hiç çalışmamış; ilk satır seçili kalıyor, ikinci satır ekleniyor,
                aradakiler atlanıyordu. Onu gösteren test bu commit'te geldi.
            */
            const from = anchor.current;
            anchor.current = index;
            setSelection((prev) => {
                const next = new Set(prev);
                // Shift-click fills in everything between the last click and this
                // one, which is the only way to select forty messages quickly.
                if (extend && from !== null) {
                    const [lo, hi] = index < from ? [index, from] : [from, index];
                    for (let i = lo; i <= hi; i++) {
                        const at = list.ids[i];
                        if (at) next.add(at);
                    }
                } else if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [list.ids],
    );

    const [picker, setPicker] = useState<{ x: number; y: number; emailId: string } | null>(null);

    const openAt = useCallback(
        (index: number) => {
            const id = list.ids[Math.max(0, Math.min(list.ids.length - 1, index))];
            if (id && account && mbox) navigate(mailboxPath(account.id, mbox, id), { replace: true });
        },
        [list.ids, account, mbox, navigate],
    );

    // When the open message leaves the folder, land on the one that took its place.
    const onLeave = useCallback(
        (id: string) => {
            if (email !== id) return;
            const at = list.ids.indexOf(id);
            if (at === -1 || !account || !mbox) return;
            const nextId = list.ids[at + 1] ?? list.ids[at - 1] ?? null;
            navigate(nextId ? mailboxPath(account.id, mbox, nextId) : mailboxPath(account.id, mbox), { replace: true });
        },
        [email, list.ids, account, mbox, navigate],
    );

    // The reader's own name for a folder — "Çöp Kutusu", not "Trash" and not an id — so the undo
    // offer names the place they can see in the sidebar.
    const folderName = useCallback(
        (mailboxId: string) => {
            const found = mailboxes.find((m) => m.id === mailboxId);
            return found ? displayLabel(found, (role) => t(`folders.role.${role}` as const)) : '';
        },
        [mailboxes, t],
    );
    const actions = useMailActions(client, account?.accountId, list, { trashId }, onLeave, folderName);

    const runBulk = useCallback(
        (what: BulkAction, mailboxId?: string) => {
            const ids = [...selection];
            setSelection(new Set());
            void actions.bulk(ids, what, mailboxId);
        },
        [selection, actions],
    );

    const closeMessage = useCallback(() => {
        if (account && mbox) navigate(mailboxPath(account.id, mbox), { replace: true });
    }, [account, mbox, navigate]);

    // Opening a message marks it read, once.
    const markedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!email || markedRef.current === email) return;
        if (!list.emails[email]) return;
        markedRef.current = email;
        actions.setSeen(email, true);
    }, [email, list.emails, actions]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            // Ctrl+K is deliberate and layout-independent, so it works from anywhere.
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyK') {
                e.preventDefault();
                focusSearch();
                return;
            }

            const target = e.target as HTMLElement | null;
            const typing = !!target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);

            /*
                Ctrl/cmd+A selects the whole list, the way it does in every mail client. It is the
                browser's "select all text" as well, so it is only taken while the person is in the
                list rather than in a field — and it is taken, not shared: selecting the words of
                fifty rows is not something anybody meant.

                Only what has been loaded, which is what the button beside the list says too. The
                folder may hold forty thousand, and quietly acting on all of them because somebody
                pressed a key would be the worst kind of surprise.
            */
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyA' && !typing && !overlayOpen()) {
                const loaded = list.ids.filter((id): id is string => Boolean(id));
                if (loaded.length) {
                    e.preventDefault();
                    setSelection(new Set(loaded));
                }
                return;
            }

            if (typing) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            /*
                The composer and anything else modal own the keyboard while they
                are open — including escape, which the composer needs so the
                draft is saved on the way out. Both register as overlays, so this
                is one question rather than one per component.
            */
            if (overlayOpen()) return;

            // Escape backs out of the open message. With the reading pane off
            // that's the only way back to the list, so it can't be optional.
            if (e.key === 'Escape' && email) {
                e.preventDefault();
                closeMessage();
                return;
            }

            // Bindings come from the keymap store, matched on physical key position.
            const at = email ? list.ids.indexOf(email) : -1;
            const action =
                e.key === 'ArrowDown' ? 'next'
                : e.key === 'ArrowUp' ? 'prev'
                : e.key === 'Delete' ? 'delete'
                : lookupRef.current(e.code);

            switch (action) {
                case 'next':
                    e.preventDefault();
                    openAt(at + 1);
                    break;
                case 'prev':
                    e.preventDefault();
                    openAt(at <= 0 ? 0 : at - 1);
                    break;
                case 'archive':
                    if (email && archiveId) actions.move(email, archiveId);
                    break;
                case 'move':
                    // Centred rather than anchored: there's no button to point at
                    // when the picker is opened from the keyboard.
                    if (email) setPicker({ x: window.innerWidth / 2 - 140, y: 120, emailId: email });
                    break;
                case 'delete':
                    if (email) actions.trash(email);
                    break;
                case 'star':
                    if (email) actions.toggleStar(email);
                    break;
                case 'unread':
                    if (email) {
                        markedRef.current = email;
                        actions.setSeen(email, false);
                    }
                    break;
                case 'compose':
                    openCompose({ mode: 'new', source: null });
                    break;
                case 'reply':
                    if (openFullRef.current) openCompose({ mode: 'reply', source: openFullRef.current });
                    break;
                case 'replyAll':
                    if (openFullRef.current) openCompose({ mode: 'replyAll', source: openFullRef.current });
                    break;
                case 'forward':
                    if (openFullRef.current) openCompose({ mode: 'forward', source: openFullRef.current });
                    break;
                case 'refresh':
                    list.reload();
                    break;
                default:
                    break;
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [email, list, openAt, actions, archiveId, closeMessage, openCompose, focusSearch]);

    // The push stream nudges the list directly; reload keeps the current folder.
    useEffect(() => {
        bumpRef.current = () => list.reload();
        dropRef.current = (emailId, mailboxId) => actions.move(emailId, mailboxId);
        return () => {
            bumpRef.current = null;
            dropRef.current = null;
        };
        // `actions` belongs here: the ref holds a function that moves mail, and a stale one would
        // move it with whatever client and account were current when it was made.
    }, [list, actions]);

    // New mail showed up in the folder we're looking at — pull it into the list.
    const seenTotalRef = useRef<number | null>(null);
    useEffect(() => {
        const total = current?.totalEmails;
        if (total === undefined) return;
        const previous = seenTotalRef.current;
        seenTotalRef.current = total;
        // And when it has been emptied from somewhere else — the folder menu — so the rows of an
        // emptied Trash do not stay on screen pointing at nothing.
        if (previous !== null && (total > previous || (total === 0 && previous > 0))) list.reload();
    }, [current?.totalEmails, list]);

    useEffect(() => {
        seenTotalRef.current = null;
    }, [mbox]);

    const openEmail = email ? list.emails[email] : undefined;
    /*
        The setting says where the reading pane goes; this says where it can go.

        On a window too narrow for both, the pane has nowhere to sit beside the
        list, so the layout falls back to the one arrangement that still works —
        the message replacing the list, which this screen already knows how to
        do. The stored setting is left alone rather than overwritten: somebody
        who reads on a phone and a desktop should not find their desktop
        arrangement changed by having opened a message on the train.
    */
    const pos = onePane ? 'off' : readingPos;
    const stacked = pos === 'bottom';
    // The reading pane only exists when something is open, so an empty mailbox
    // gives the list the whole window instead of half of it plus a placeholder.
    const showReader = pos !== 'off' && Boolean(email);
    // Reading pane off: the message replaces the list rather than sharing with it.
    const soloReader = pos === 'off' && Boolean(email);
    const paneWidth = layout === 'wide' ? Math.min(listWidth, 340) : listWidth;

    return (
        <>

            <div style={{ flex: 1, display: 'flex', flexDirection: stacked ? 'column' : 'row', minWidth: 0 }}>
                <section
                    style={{
                        display: soloReader ? 'none' : 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        ...(!showReader
                            ? { flex: 1 }
                            : stacked
                              ? { height: listHeight, flex: '0 0 auto', borderBottom: '1px solid var(--border)' }
                              : {
                                    width: paneWidth,
                                    flex: '0 0 auto',
                                    borderRight: '1px solid var(--border)',
                                }),
                    }}
                >
                    {/*
                        Tek satırlık 48px başlık. Sıralama eskiden klasör adının ALTINDA kendi
                        satırındaydı ve arama kutusu da buradaydı; arama 1. adımda üst çubuğa
                        çıkınca ikinci satırı tutan bir şey kalmadı.
                    */}
                    <div
                        style={{
                            flex: '0 0 auto',
                            height: 48,
                            padding: '0 14px',
                            borderBottom: '1px solid var(--border)',
                            background: 'var(--surface2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        {/* The drawer's button belongs beside the folder name, not in a
                            bar of its own: a phone has no forty pixels of height to spare
                            for a second header saying what this one already says. */}
                        <NavButton />
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div
                                style={{
                                    fontSize: 'var(--fs-lg)',
                                    fontWeight: 600,
                                    letterSpacing: '-0.2px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {search.trim()
                                    ? t('list.searchTitle')
                                    : current
                                      ? displayLabel(current, (role) => t(`folders.role.${role}` as const))
                                      : config().productName}
                            </div>
                            {/*
                                Mono kicker değil, düz metin: `// 128 konuşma` terminal
                                estetiğiydi ve mono artık yalnız hizalamanın işe yaradığı
                                yerlerde (sayı, tarih, boyut, kısayol) kalıyor.
                            */}
                            <div
                                data-testid="list-count"
                                style={{
                                    fontSize: 'var(--fs-xs)',
                                    color: 'var(--text-mute)',
                                    marginTop: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {search.trim()
                                    ? `${
                                          list.loading
                                              ? t('list.searching')
                                              : t(list.total === 1 ? 'list.resultsOne' : 'list.results', { count: num(list.total) })
                                      } — ${
                                          /^all:/i.test(search.trim()) ? t('list.inAllMail') : (current?.name ?? t('list.inThisFolder'))
                                      }`
                                    : /*
                                          Grouped, the total counts conversations, not messages —
                                          saying "7 messages" over seven conversations holding nine
                                          would be a plain untruth. The unread figure beside it is
                                          the folder's own and stays in messages either way.
                                      */
                                      `${t(threads ? 'list.conversations' : 'list.messages', { count: num(list.total) })}${
                                          current?.unreadEmails ? ` · ${t('list.unread', { count: num(current.unreadEmails) })}` : ''
                                      }`}
                            </div>
                        </div>

                        <select
                            value={`${sort.field}:${sort.ascending ? 'asc' : 'desc'}`}
                            onChange={(e) => {
                                const [field, dir] = e.target.value.split(':');
                                setSort({ field: field as SortField, ascending: dir === 'asc' });
                            }}
                            title={t('sort.title')}
                            data-testid="sort"
                            data-hit
                            style={{
                                height: 28,
                                maxWidth: 132,
                                minWidth: 0,
                                flex: '0 1 auto',
                                padding: '0 4px',
                                background: 'transparent',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-btn)',
                                color: 'var(--text-dim)',
                                fontSize: 'var(--fs-sm)',
                            }}
                        >
                            {SORTS.flatMap((field) =>
                                [false, true].map((ascending) => (
                                    <option key={`${field}:${ascending}`} value={`${field}:${ascending ? 'asc' : 'desc'}`}>
                                        {t(`sort.${field}` as const)} {ascending ? '↑' : '↓'}
                                    </option>
                                )),
                            )}
                        </select>

                        {/*
                            Yenilemenin bugüne kadar yalnız klavye kısayolu vardı; listenin
                            kendi başlığı onu isteyen kişinin baktığı yer.
                        */}
                        <button
                            data-testid="list-refresh"
                            data-hit
                            onClick={() => list.reload()}
                            title={t('action.refresh')}
                            aria-label={t('action.refresh')}
                            style={{
                                width: 28,
                                height: 28,
                                flex: '0 0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'transparent',
                                border: '1px solid var(--border)',
                                borderRadius: 'var(--r-btn)',
                                color: 'var(--text-dim)',
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 11a8 8 0 1 0-.7 4.5M20 5v6h-6" />
                            </svg>
                        </button>
                    </div>
                    {/* Terms this server can't answer. Saying nothing would make
                        the results look complete when they aren't. */}
                    {list.ignored.length ? (
                        <div style={{ padding: '6px 12px', background: 'var(--amber-bg)', borderBottom: '1px solid var(--amber)', fontSize: 'var(--fs-xs)', color: 'var(--amber)' }}>
                            {t('search.ignored', { list: list.ignored.join(', ') })}
                        </div>
                    ) : null}

                    {/*
                        Emptying the Trash or Junk, over the list of what would go — where people
                        look for it. The folder menu offers the same, through the same dialog.
                    */}
                    {!search.trim() && current && (current.id === trashId || current.id === junkId) && list.total > 0 && client ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 12px', borderBottom: '1px solid var(--border)' }}>
                            <button
                                data-testid="list-empty-folder"
                                data-hit
                                onClick={() => setEmptying(true)}
                                style={{ background: 'transparent', border: 'none', padding: '2px 0', color: 'var(--red)', fontSize: 'var(--fs-xs)', fontWeight: 600 }}
                            >
                                {t('list.emptyFolder')}
                            </button>
                        </div>
                    ) : null}
                    {emptying && current && client ? (
                        <EmptyFolderDialog
                            client={client}
                            mailbox={current}
                            label={displayLabel(current, (role) => t(`folders.role.${role}` as const))}
                            onClose={() => setEmptying(false)}
                            onEmptied={() => {
                                reloadFolders();
                                list.reload();
                                closeMessage();
                            }}
                        />
                    ) : null}

                    {selection.size ? (
                        <div
                            data-testid="bulk-bar"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                flexWrap: 'wrap',
                                padding: '7px 12px',
                                background: 'var(--accent-bg)',
                                borderBottom: '1px solid var(--border)',
                            }}
                        >
                            {/* Mono kicker değil: mono artık yalnız sayı, tarih, boyut, sunucu
                                yolu ve kısayol için — hizalamanın işe yaradığı yerler. */}
                            <span style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-dim)' }}>
                                {t('bulk.selected', { count: num(selection.size) })}
                            </span>
                            {/*
                                Everything fetched so far, not everything that
                                matches. A "select all 2,948" that quietly turns
                                into three thousand requests is how a mailbox
                                gets wrecked by one click, so the offer is only
                                ever for what is actually on screen and it says
                                how many that is.
                            */}
                            <button
                                data-testid="select-loaded"
                                onClick={() => setSelection(new Set(list.ids.filter((id): id is string => Boolean(id))))}
                                style={bulkButton}
                            >
                                {t('bulk.selectLoaded', { count: num(list.ids.filter(Boolean).length) })}
                            </button>
                            <div style={{ flex: 1 }} />
                            <button onClick={() => runBulk('read')} style={bulkButton}>{t('action.markRead')}</button>
                            <button onClick={() => runBulk('unread')} style={bulkButton}>{t('action.markUnread')}</button>
                            <button onClick={() => runBulk('star')} style={bulkButton}>{t('action.star')}</button>
                            {archiveId ? (
                                <button onClick={() => runBulk('move', archiveId)} style={bulkButton}>{t('action.archive')}</button>
                            ) : null}
                            <button
                                onClick={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setPicker({ x: r.left, y: r.bottom + 4, emailId: '' });
                                }}
                                style={bulkButton}
                            >
                                {t('action.move')}
                            </button>
                            <button onClick={() => runBulk('trash')} style={bulkButton}>{t('action.trash')}</button>
                            <button onClick={() => setSelection(new Set())} style={bulkButton}>{t('bulk.clear')}</button>
                        </div>
                    ) : null}

                    <MessageList
                        list={list}
                        // Gönderilmiş klasöründe satır göndereni değil alıcıyı gösterir.
                        showRecipient={!!sentId && mbox === sentId}
                        selectedId={email ?? null}
                        selection={selection}
                        onToggleSelect={toggleSelect}
                        onOpen={(id) => {
                            // A draft opens in the composer, not the reader — reading your own
                            // half-written message is never what you meant to do.
                            if (list.emails[id]?.keywords[KEYWORD.draft] && client && account) {
                                void client.getEmailFull(account.accountId, id).then((full) => {
                                    if (full) openCompose({ mode: 'draft', source: full });
                                });
                                return;
                            }
                            // Clicking the open message again closes the pane.
                            if (id === email) {
                                closeMessage();
                                return;
                            }
                            if (account && mbox) navigate(mailboxPath(account.id, mbox, id));
                        }}
                    />
                </section>

                {showReader || soloReader ? (
                    <>
                    {showReader ? (
                    <Resizer
                        axis={stacked ? 'y' : 'x'}
                        onResize={(d) =>
                            stacked
                                ? setPaneSize({ listHeight: clamp(listHeight + d, 160, window.innerHeight - 200) })
                                : setPaneSize({ listWidth: clamp(listWidth + d, 280, window.innerWidth - 420) })
                        }
                    />
                    ) : null}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {soloReader ? (
                            <button
                                data-hit
                                onClick={closeMessage}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 7,
                                    alignSelf: 'flex-start',
                                    margin: '8px 12px 0',
                                    padding: '4px 10px',
                                    background: 'transparent',
                                    border: '1px solid var(--border2)',
                                    borderRadius: 'var(--r-btn)',
                                    color: 'var(--text-dim)',
                                    fontSize: 'var(--fs-sm)',
                                }}
                            >
                                ← {t('reader.back', { name: current ? displayLabel(current, (role) => t(`folders.role.${role}` as const)) : '' })}
                                <span className="kicker">esc</span>
                            </button>
                        ) : null}
                        <Toolbar
                            visible={Boolean(email)}
                            starred={Boolean(openEmail?.keywords[KEYWORD.flagged])}
                            unread={openEmail ? rowUnread(openEmail) : false}
                            canArchive={Boolean(archiveId)}
                            onArchive={() => email && archiveId && actions.move(email, archiveId)}
                            onMove={(anchor) => email && setPicker({ ...anchor, emailId: email })}
                            onTrash={() => email && actions.trash(email)}
                            onStar={() => email && actions.toggleStar(email)}
                            onToggleSeen={() => {
                                if (!email) return;
                                markedRef.current = email;
                                actions.toggleSeen(email);
                            }}
                            onReply={() => openFullRef.current && openCompose({ mode: 'reply', source: openFullRef.current })}
                            onReplyAll={() => openFullRef.current && openCompose({ mode: 'replyAll', source: openFullRef.current })}
                            onForward={() => openFullRef.current && openCompose({ mode: 'forward', source: openFullRef.current })}
                        />
                        {actions.error ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '8px 14px',
                                    background: 'var(--red-bg)',
                                    borderBottom: '1px solid var(--red)',
                                    color: 'var(--red)',
                                    fontSize: 'var(--fs-sm)',
                                }}
                            >
                                <span style={{ flex: 1, minWidth: 0 }}>{actions.error}</span>
                                <button
                                    onClick={actions.clearError}
                                    style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: 'var(--fs-lg)' }}
                                >
                                    ×
                                </button>
                            </div>
                        ) : null}
                        <Reader
                            client={client}
                            accountId={account?.accountId}
                            emailId={email ?? null}
                            threads={threads}
                            junkId={junkId}
                            sentId={sentId}
                            onLoaded={(full) => {
                                openFullRef.current = full;
                            }}
                            onSpam={junkId && email ? () => actions.move(email, junkId) : undefined}
                            onNotSpam={inboxId && email ? () => actions.move(email, inboxId) : undefined}
                            onOpen={account ? (m) => navigate(mailboxPath(account.id, m.folder, m.id)) : undefined}
                        />
                    </div>
                    </>
                ) : null}
            </div>

            {picker ? (
                <FolderPicker
                    mailboxes={mailboxes}
                    excludeId={mbox}
                    anchor={{ x: picker.x, y: picker.y }}
                    onPick={(mailboxId) => (picker.emailId ? actions.move(picker.emailId, mailboxId) : runBulk('move', mailboxId))}
                    onCreate={(name) => {
                        if (!client || !account) return;
                        void client
                            .createMailbox(account.accountId, { name })
                            .then((folder) => {
                                reloadFolders();
                                if (picker.emailId) actions.move(picker.emailId, folder.id);
                                else runBulk('move', folder.id);
                            })
                            .catch(() => {
                                // useMailActions owns the visible error surface; a
                                // failure here leaves the message exactly where it was.
                                reloadFolders();
                            });
                    }}
                    onClose={() => setPicker(null)}
                />
            ) : null}
        </>
    );
}
